import { useEffect, useMemo, useState } from "react"

import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from "@mui/material"

import ReceiptLongRoundedIcon from "@mui/icons-material/ReceiptLongRounded"
import LockRoundedIcon from "@mui/icons-material/LockRounded"

import MainLayout from "../../layouts/MainLayout"

import {
  createSupplierInvoice,
  getSupplierInvoiceFormData,
  getSupplierInvoices,
  validateSupplierInvoice,
} from "../../services/supplierInvoiceService"


// =========================================================
// HELPERS
// =========================================================

const today = () => {
  return new Date()
    .toISOString()
    .slice(0, 10)
}


const addDays = (
  dateString,
  days
) => {
  if (!dateString) {
    return today()
  }

  const date =
    new Date(
      `${dateString}T00:00:00`
    )

  date.setDate(
    date.getDate() +
      Number(days || 0)
  )

  return date
    .toISOString()
    .slice(0, 10)
}


const formatAmount = (
  value
) => {
  return Number(
    value || 0
  ).toLocaleString(
    "fr-FR",
    {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }
  )
}


const defaultFormData = {
  organization_id: 1,

  supplier_id: "",
  purchase_order_id: "",
  purchase_receipt_id: "",

  supplier_invoice_number: "",

  invoice_date: today(),
  due_date: today(),

  currency: "USD",
  exchange_rate: 1,

  discount_amount: 0,
  tax_amount: 0,
  transport_cost: 0,
  other_cost: 0,

  notes: "",

  items: [],
}


// =========================================================
// KPI CARD
// =========================================================

function MetricCard({
  label,
  value,
  caption,
  accent = "#2563eb",
}) {
  return (
    <Paper
      variant="outlined"
      sx={{
        p: {
          xs: 1.75,
          sm: 2,
        },

        minWidth: 0,
        minHeight: 106,

        borderRadius: 3.5,

        borderLeft:
          "4px solid",

        borderLeftColor:
          accent,

        boxShadow:
          "0 8px 24px rgba(15, 23, 42, 0.04)",
      }}
    >
      <Typography
        variant="caption"
        sx={{
          color:
            "text.secondary",

          fontWeight: 800,

          letterSpacing:
            0.25,
        }}
      >
        {label}
      </Typography>

      <Typography
        sx={{
          mt: 0.35,

          fontSize: {
            xs: 23,
            md: 27,
          },

          lineHeight: 1.15,

          fontWeight: 900,

          color: "#0f172a",
        }}
      >
        {value}
      </Typography>

      {caption ? (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{
            display: "block",
            mt: 0.6,
          }}
        >
          {caption}
        </Typography>
      ) : null}
    </Paper>
  )
}


// =========================================================
// PAGE
// =========================================================

function SupplierInvoicesPage() {
  const [
    invoices,
    setInvoices,
  ] = useState([])

  const [
    receipts,
    setReceipts,
  ] = useState([])

  const [
    organizations,
    setOrganizations,
  ] = useState([])

  const [
    open,
    setOpen,
  ] = useState(false)

  const [
    loading,
    setLoading,
  ] = useState(false)

  const [
    saving,
    setSaving,
  ] = useState(false)

  const [
    searchTerm,
    setSearchTerm,
  ] = useState("")

  const [
    page,
    setPage,
  ] = useState(0)

  const rowsPerPage = 5

  const [
    formData,
    setFormData,
  ] = useState({
    ...defaultFormData,
  })

  const [
    selectedReceipt,
    setSelectedReceipt,
  ] = useState(null)

  const [
    snackbar,
    setSnackbar,
  ] = useState({
    open: false,
    severity: "success",
    message: "",
  })


  // =======================================================
  // PAGE RESET
  // =======================================================

  useEffect(() => {
    setPage(0)
  }, [searchTerm])


  // =======================================================
  // SNACKBAR
  // =======================================================

  const showSnackbar = (
    message,
    severity = "success"
  ) => {
    setSnackbar({
      open: true,
      severity,
      message,
    })
  }


  // =======================================================
  // RESET FORM
  // =======================================================

  const resetForm = () => {
    setSelectedReceipt(
      null
    )

    setFormData({
      ...defaultFormData,

      invoice_date:
        today(),

      due_date:
        today(),

      items: [],
    })
  }


  // =======================================================
  // LOAD DATA
  // =======================================================

  const loadData =
    async () => {
      try {
        setLoading(true)

        const [
          invoiceList,
          form,
        ] =
          await Promise.all([
            getSupplierInvoices(),

            getSupplierInvoiceFormData(),
          ])

        setInvoices(
          Array.isArray(
            invoiceList
          )
            ? invoiceList
            : []
        )

        setReceipts(
          Array.isArray(
            form?.receipts
          )
            ? form.receipts
            : []
        )

        setOrganizations(
          Array.isArray(
            form?.organizations
          )
            ? form.organizations
            : []
        )
      } catch (error) {
        console.error(
          error
        )

        showSnackbar(
          "Impossible de charger les factures fournisseurs.",
          "error"
        )
      } finally {
        setLoading(false)
      }
    }


  useEffect(() => {
    loadData()
  }, [])


  // =======================================================
  // DIALOG
  // =======================================================

  const handleOpen = () => {
    resetForm()
    setOpen(true)
  }


  const handleClose = () => {
    if (saving) {
      return
    }

    setOpen(false)
    resetForm()
  }


  // =======================================================
  // RECEIPT SELECTION
  // =======================================================

  const handleReceiptChange =
    (
      receiptId
    ) => {
      const receipt =
        receipts.find(
          (item) =>
            Number(item.id) ===
            Number(receiptId)
        )

      if (!receipt) {
        resetForm()
        return
      }

      setSelectedReceipt(
        receipt
      )

      const invoiceDate =
        today()

      const dueDate =
        addDays(
          invoiceDate,
          receipt.payment_terms_days ||
            0
        )

      setFormData(
        (previous) => ({
          ...previous,

          organization_id:
            Number(
              receipt.organization_id ||
                previous.organization_id ||
                1
            ),

          supplier_id:
            Number(
              receipt.supplier_id
            ),

          purchase_order_id:
            Number(
              receipt.purchase_order_id
            ),

          purchase_receipt_id:
            Number(
              receipt.id
            ),

          invoice_date:
            invoiceDate,

          due_date:
            dueDate,

          currency:
            receipt.currency ||
            "USD",

          exchange_rate:
            Number(
              receipt.exchange_rate ||
                1
            ),

          notes:
            `Facture liée à la réception ${receipt.code} et au bon de commande ${receipt.purchase_order_code}.`,

          items:
            (
              receipt.items ||
              []
            ).map(
              (item) => ({
                product_id:
                  Number(
                    item.product_id
                  ),

                description:
                  item.notes ||
                  "",

                product_code:
                  item.product_code,

                product_name:
                  item.product_name,

                unit:
                  item.unit ||
                  "piece",

                quantity:
                  Number(
                    item.accepted_quantity ||
                      0
                  ),

                accepted_quantity:
                  Number(
                    item.accepted_quantity ||
                      0
                  ),

                unit_cost:
                  Number(
                    item.unit_cost ||
                      0
                  ),

                discount_percent:
                  Number(
                    item.discount_percent ||
                      0
                  ),

                tax_percent:
                  Number(
                    item.tax_percent ||
                      0
                  ),
              })
            ),
        })
      )
    }


  // =======================================================
  // FORM CHANGE
  // =======================================================

  const handleChange =
    (
      event
    ) => {
      const {
        name,
        value,
      } = event.target

      const numericFields =
        [
          "organization_id",
          "exchange_rate",
          "discount_amount",
          "tax_amount",
          "transport_cost",
          "other_cost",
        ]

      setFormData(
        (previous) => ({
          ...previous,

          [name]:
            numericFields.includes(
              name
            )
              ? value === ""
                ? ""
                : Number(
                    value
                  )
              : value,
        })
      )
    }


  // =======================================================
  // ITEM CHANGE
  // =======================================================

  const handleItemChange =
    (
      index,
      field,
      value
    ) => {
      setFormData(
        (previous) => ({
          ...previous,

          items:
            previous.items.map(
              (
                item,
                currentIndex
              ) =>
                currentIndex ===
                index
                  ? {
                      ...item,

                      [field]:
                        value === ""
                          ? ""
                          : Number(
                              value
                            ),
                    }
                  : item
            ),
        })
      )
    }


  // =======================================================
  // TOTALS
  // =======================================================

  const itemsSubtotal =
    useMemo(() => {
      return formData.items.reduce(
        (
          sum,
          item
        ) => {
          const quantity =
            Number(
              item.quantity ||
                0
            )

          const unitCost =
            Number(
              item.unit_cost ||
                0
            )

          const gross =
            quantity *
            unitCost

          const discount =
            gross *
            (
              Number(
                item.discount_percent ||
                  0
              ) /
              100
            )

          const taxable =
            gross -
            discount

          const tax =
            taxable *
            (
              Number(
                item.tax_percent ||
                  0
              ) /
              100
            )

          return (
            sum +
            taxable +
            tax
          )
        },
        0
      )
    }, [
      formData.items,
    ])


  const total =
    useMemo(() => {
      return (
        itemsSubtotal -
        Number(
          formData.discount_amount ||
            0
        ) +
        Number(
          formData.tax_amount ||
            0
        ) +
        Number(
          formData.transport_cost ||
            0
        ) +
        Number(
          formData.other_cost ||
            0
        )
      )
    }, [
      itemsSubtotal,
      formData.discount_amount,
      formData.tax_amount,
      formData.transport_cost,
      formData.other_cost,
    ])


  // =======================================================
  // SAVE
  // =======================================================

  const handleSave =
    async () => {
      if (
        !formData.purchase_receipt_id
      ) {
        showSnackbar(
          "Sélectionnez une réception fournisseur contrôlée.",
          "warning"
        )

        return
      }

      if (
        !formData
          .supplier_invoice_number
          .trim()
      ) {
        showSnackbar(
          "Saisissez le numéro réel de la facture du fournisseur.",
          "warning"
        )

        return
      }

      if (
        !formData.items.length
      ) {
        showSnackbar(
          "Aucune quantité acceptée n'est disponible pour cette réception.",
          "warning"
        )

        return
      }

      const invalidItem =
        formData.items.some(
          (item) =>
            !item.product_id ||
            Number(
              item.quantity ||
                0
            ) <= 0 ||
            Number(
              item.quantity ||
                0
            ) >
              Number(
                item.accepted_quantity ||
                  0
              )
        )

      if (invalidItem) {
        showSnackbar(
          "La quantité facturée doit être comprise dans la quantité réellement acceptée.",
          "warning"
        )

        return
      }

      const payload = {
        organization_id:
          Number(
            formData.organization_id ||
              1
          ),

        supplier_id:
          Number(
            formData.supplier_id
          ),

        purchase_order_id:
          Number(
            formData.purchase_order_id
          ),

        purchase_receipt_id:
          Number(
            formData.purchase_receipt_id
          ),

        supplier_invoice_number:
          formData
            .supplier_invoice_number
            .trim(),

        invoice_date:
          formData.invoice_date,

        due_date:
          formData.due_date,

        currency:
          formData.currency,

        exchange_rate:
          Number(
            formData.exchange_rate ||
              1
          ),

        discount_amount:
          Number(
            formData.discount_amount ||
              0
          ),

        tax_amount:
          Number(
            formData.tax_amount ||
              0
          ),

        transport_cost:
          Number(
            formData.transport_cost ||
              0
          ),

        other_cost:
          Number(
            formData.other_cost ||
              0
          ),

        notes:
          formData.notes
            ?.trim() ||
          null,

        items:
          formData.items.map(
            (item) => ({
              product_id:
                Number(
                  item.product_id
                ),

              description:
                item.description
                  ?.trim() ||
                null,

              quantity:
                Number(
                  item.quantity
                ),

              unit_cost:
                Number(
                  item.unit_cost ||
                    0
                ),

              discount_percent:
                Number(
                  item.discount_percent ||
                    0
                ),

              tax_percent:
                Number(
                  item.tax_percent ||
                    0
                ),
            })
          ),
      }

      try {
        setSaving(true)

        const result =
          await createSupplierInvoice(
            payload
          )

        showSnackbar(
          `Facture ${result?.code || ""} créée avec succès.`
        )

        setOpen(false)

        resetForm()

        await loadData()
      } catch (error) {
        console.error(
          error
        )

        showSnackbar(
          error?.response
            ?.data
            ?.detail ||
            "Impossible de créer la facture fournisseur.",
          "error"
        )
      } finally {
        setSaving(false)
      }
    }


  // =======================================================
  // VALIDATE
  // =======================================================

  const handleValidate =
    async (
      invoiceId
    ) => {
      const confirmed =
        window.confirm(
          "Valider cette facture ? La dette envers le fournisseur sera officiellement reconnue."
        )

      if (!confirmed) {
        return
      }

      try {
        await validateSupplierInvoice(
          invoiceId
        )

        showSnackbar(
          "Facture validée. La dette fournisseur est maintenant ouverte au paiement."
        )

        await loadData()
      } catch (error) {
        console.error(
          error
        )

        showSnackbar(
          error?.response
            ?.data
            ?.detail ||
            "Impossible de valider la facture.",
          "error"
        )
      }
    }


  // =======================================================
  // FILTER
  // =======================================================

  const filteredInvoices =
    useMemo(() => {
      const query =
        searchTerm
          .trim()
          .toLowerCase()

      if (!query) {
        return invoices
      }

      return invoices.filter(
        (invoice) => {
          const text =
            [
              invoice.code,
              invoice.supplier_invoice_number,
              invoice.supplier_name,
              invoice.purchase_order_code,
              invoice.purchase_receipt_code,
            ]
              .filter(Boolean)
              .join(" ")
              .toLowerCase()

          return text.includes(
            query
          )
        }
      )
    }, [
      invoices,
      searchTerm,
    ])


  const paginatedInvoices =
    useMemo(() => {
      const start =
        page *
        rowsPerPage

      return filteredInvoices.slice(
        start,
        start +
          rowsPerPage
      )
    }, [
      filteredInvoices,
      page,
    ])


  // =======================================================
  // STATUS
  // =======================================================

  const statusInfo = (
    status
  ) => {
    switch (
      String(
        status ||
          ""
      ).toUpperCase()
    ) {
      case "UNPAID":
        return {
          label:
            "À payer",

          color:
            "warning",
        }

      case "PARTIALLY_PAID":
        return {
          label:
            "Partiellement payée",

          color:
            "info",
        }

      case "PAID":
        return {
          label:
            "Payée",

          color:
            "success",
        }

      case "CANCELLED":
        return {
          label:
            "Annulée",

          color:
            "error",
        }

      case "DRAFT":
      default:
        return {
          label:
            "Brouillon",

          color:
            "default",
        }
    }
  }


  // =======================================================
  // KPI
  // =======================================================

  const unpaidCount =
    useMemo(
      () =>
        invoices.filter(
          (invoice) =>
            [
              "UNPAID",
              "PARTIALLY_PAID",
            ].includes(
              String(
                invoice.status ||
                  ""
              ).toUpperCase()
            )
        ).length,
      [invoices]
    )


  const paidCount =
    useMemo(
      () =>
        invoices.filter(
          (invoice) =>
            String(
              invoice.status ||
                ""
            ).toUpperCase() ===
            "PAID"
        ).length,
      [invoices]
    )


  const remainingTotal =
    useMemo(
      () =>
        invoices.reduce(
          (
            sum,
            invoice
          ) =>
            sum +
            Number(
              invoice.remaining_amount ??
                invoice.total_amount ??
                0
            ),
          0
        ),
      [invoices]
    )


  // =======================================================
  // RENDER
  // =======================================================

  return (
    <MainLayout>
      <Box
        sx={{
          minWidth: 0,

          pb: {
            xs: 2,
            md: 4,
          },

          "& .MuiPaper-root":
            {
              borderColor:
                "rgba(148, 163, 184, 0.24)",

              backgroundImage:
                "none",
            },

          "& .MuiButton-root":
            {
              textTransform:
                "none",

              fontWeight:
                800,

              borderRadius:
                2.5,
            },

          "& .MuiTextField-root .MuiOutlinedInput-root":
            {
              borderRadius:
                2.5,
            },

          "& .MuiAlert-root":
            {
              borderRadius:
                3,
            },

          "& .MuiTableHead-root .MuiTableCell-root":
            {
              backgroundColor:
                "#f8fafc",

              color:
                "#334155",

              fontWeight:
                800,

              whiteSpace:
                "nowrap",
            },

          "& .MuiTableBody-root .MuiTableRow-root:hover":
            {
              backgroundColor:
                "#f8fbff",
            },

          "& .MuiChip-root":
            {
              fontWeight:
                700,
            },
        }}
      >

        {/* =================================================
            HEADER
        ================================================= */}

        <Box
          sx={{
            display:
              "grid",

            gridTemplateColumns:
              {
                xs:
                  "1fr",

                md:
                  "minmax(0, 1fr) auto",
              },

            alignItems:
              "start",

            columnGap:
              2,

            rowGap:
              1.25,

            mb: 2.5,

            width:
              "100%",
          }}
        >
          <Box
            sx={{
              minWidth: 0,
            }}
          >
            <Typography
              variant="h4"
              sx={{
                color:
                  "#0f172a",

                fontWeight:
                  900,

                fontSize:
                  {
                    xs:
                      "1.65rem",

                    sm:
                      "1.95rem",

                    md:
                      "2.15rem",
                  },

                lineHeight:
                  1.12,

                letterSpacing:
                  "-0.02em",
              }}
            >
              Factures fournisseurs
            </Typography>

            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                mt: 0.5,

                maxWidth:
                  780,

                lineHeight:
                  1.55,
              }}
            >
              Contrôlez les factures reçues, validez les montants et suivez les dettes fournisseurs jusqu'au paiement.
            </Typography>
          </Box>


          <Box
            sx={{
              display:
                "flex",

              justifyContent:
                {
                  xs:
                    "stretch",

                  md:
                    "flex-end",
                },

              justifySelf:
                {
                  xs:
                    "stretch",

                  md:
                    "end",
                },

              width:
                {
                  xs:
                    "100%",

                  md:
                    "auto",
                },

              pt:
                {
                  xs:
                    0.5,

                  md:
                    0,
                },
            }}
          >
            <Button
              variant="contained"
              startIcon={
                <ReceiptLongRoundedIcon />
              }
              onClick={
                handleOpen
              }
              sx={{
                width:
                  {
                    xs:
                      "100%",

                    sm:
                      "auto",
                  },

                minHeight:
                  44,

                px:
                  2.5,

                whiteSpace:
                  "nowrap",

                boxShadow:
                  "0 8px 20px rgba(37, 99, 235, 0.18)",
              }}
            >
              Nouvelle facture
            </Button>
          </Box>
        </Box>


        {/* =================================================
            KPI
        ================================================= */}

        <Box
          sx={{
            display:
              "grid",

            gridTemplateColumns:
              {
                xs:
                  "1fr",

                sm:
                  "repeat(2, minmax(0, 1fr))",

                xl:
                  "repeat(4, minmax(0, 1fr))",
              },

            gap:
              1.5,

            mb:
              2,
          }}
        >
          <MetricCard
            label="Factures"
            value={
              invoices.length
            }
            caption="Total enregistré"
          />

          <MetricCard
            label="À payer"
            value={
              unpaidCount
            }
            caption="Dettes fournisseurs ouvertes"
            accent="#f59e0b"
          />

          <MetricCard
            label="Payées"
            value={
              paidCount
            }
            caption="Factures soldées"
            accent="#16a34a"
          />

          <MetricCard
            label="Reste à payer"
            value={
              formatAmount(
                remainingTotal
              )
            }
            caption="Montant encore ouvert"
            accent="#dc2626"
          />
        </Box>


        {/* =================================================
            INFO
        ================================================= */}

        <Alert
          severity="info"
          sx={{
            mb: 2,
          }}
        >
          Une facture fournisseur est créée à partir d'une réception contrôlée.
          Le fournisseur, le bon de commande, les produits et les quantités sont
          repris automatiquement par RLSys.
        </Alert>


        {/* =================================================
            SEARCH
        ================================================= */}

        <Paper
          variant="outlined"
          sx={{
            p: {
              xs:
                1.25,

              sm:
                1.5,
            },

            mb:
              2,

            borderRadius:
              3.5,
          }}
        >
          <TextField
            fullWidth
            size="small"
            placeholder="Rechercher une facture, un fournisseur, un bon de commande ou une réception..."
            value={
              searchTerm
            }
            onChange={(
              event
            ) =>
              setSearchTerm(
                event.target
                  .value
              )
            }
          />
        </Paper>


        {/* =================================================
            TABLE
        ================================================= */}

        <Paper
          variant="outlined"
          sx={{
            overflow:
              "hidden",

            borderRadius:
              3.5,

            boxShadow:
              "0 10px 28px rgba(15, 23, 42, 0.04)",
          }}
        >
          <Box
            sx={{
              px: {
                xs:
                  1.5,

                sm:
                  2,
              },

              py:
                1.5,

              borderBottom:
                "1px solid",

              borderColor:
                "divider",
            }}
          >
            <Typography
              fontWeight={
                900
              }
              color="#0f172a"
            >
              Registre des factures fournisseurs
            </Typography>

            <Typography
              variant="caption"
              color="text.secondary"
            >
              Historique des factures, échéances, paiements et soldes fournisseurs.
            </Typography>
          </Box>


          <Box
            sx={{
              overflowX:
                "auto",
            }}
          >
            <Table
              size="small"
              sx={{
                minWidth:
                  1120,
              }}
            >
              <TableHead>
                <TableRow>
                  <TableCell>
                    Facture
                  </TableCell>

                  <TableCell>
                    Fournisseur
                  </TableCell>

                  <TableCell>
                    Réception
                  </TableCell>

                  <TableCell>
                    Date
                  </TableCell>

                  <TableCell>
                    Échéance
                  </TableCell>

                  <TableCell
                    align="right"
                  >
                    Total
                  </TableCell>

                  <TableCell
                    align="right"
                  >
                    Payé
                  </TableCell>

                  <TableCell
                    align="right"
                  >
                    Reste
                  </TableCell>

                  <TableCell>
                    Statut
                  </TableCell>

                  <TableCell
                    align="right"
                  >
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>


              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell
                      colSpan={
                        10
                      }
                      align="center"
                      sx={{
                        py:
                          5,
                      }}
                    >
                      Chargement...
                    </TableCell>
                  </TableRow>
                ) : filteredInvoices.length ===
                  0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={
                        10
                      }
                      align="center"
                      sx={{
                        py:
                          5,

                        color:
                          "text.secondary",
                      }}
                    >
                      Aucune facture fournisseur.
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedInvoices.map(
                    (
                      invoice
                    ) => {
                      const info =
                        statusInfo(
                          invoice.status
                        )

                      return (
                        <TableRow
                          key={
                            invoice.id
                          }
                          hover
                        >
                          <TableCell>
                            <Typography
                              fontWeight={
                                700
                              }
                              sx={{
                                whiteSpace:
                                  "nowrap",
                              }}
                            >
                              {invoice.supplier_invoice_number ||
                                invoice.code ||
                                "-"}
                            </Typography>

                            {invoice.supplier_invoice_number &&
                            invoice.code ? (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                {invoice.code}
                              </Typography>
                            ) : null}
                          </TableCell>

                          <TableCell>
                            {invoice.supplier_name ||
                              "-"}
                          </TableCell>

                          <TableCell>
                            {invoice.purchase_receipt_code ||
                              "-"}
                          </TableCell>

                          <TableCell>
                            {invoice.invoice_date
                              ? String(
                                  invoice.invoice_date
                                ).slice(
                                  0,
                                  10
                                )
                              : "-"}
                          </TableCell>

                          <TableCell>
                            {invoice.due_date
                              ? String(
                                  invoice.due_date
                                ).slice(
                                  0,
                                  10
                                )
                              : "-"}
                          </TableCell>

                          <TableCell
                            align="right"
                          >
                            <Typography
                              fontWeight={
                                800
                              }
                              sx={{
                                whiteSpace:
                                  "nowrap",
                              }}
                            >
                              {formatAmount(
                                invoice.total_amount
                              )}{" "}
                              {invoice.currency ||
                                "USD"}
                            </Typography>
                          </TableCell>

                          <TableCell
                            align="right"
                          >
                            {formatAmount(
                              invoice.paid_amount
                            )}
                          </TableCell>

                          <TableCell
                            align="right"
                          >
                            {formatAmount(
                              invoice.remaining_amount ??
                                invoice.total_amount ??
                                0
                            )}
                          </TableCell>

                          <TableCell>
                            <Chip
                              size="small"
                              label={
                                info.label
                              }
                              color={
                                info.color
                              }
                            />
                          </TableCell>

                          <TableCell
                            align="right"
                          >
                            {String(
                              invoice.status ||
                                ""
                            ).toUpperCase() ===
                            "DRAFT" ? (
                              <Button
                                size="small"
                                variant="contained"
                                color="success"
                                onClick={() =>
                                  handleValidate(
                                    invoice.id
                                  )
                                }
                              >
                                Valider
                              </Button>
                            ) : (
                              "-"
                            )}
                          </TableCell>
                        </TableRow>
                      )
                    }
                  )
                )}
              </TableBody>
            </Table>
          </Box>


          <TablePagination
            component="div"
            count={
              filteredInvoices.length
            }
            page={
              page
            }
            onPageChange={(
              _,
              newPage
            ) =>
              setPage(
                newPage
              )
            }
            rowsPerPage={
              rowsPerPage
            }
            rowsPerPageOptions={[
              5,
            ]}
            labelRowsPerPage="Lignes"
            labelDisplayedRows={({
              from,
              to,
              count,
            }) =>
              `${from}–${to} sur ${count}`
            }
            sx={{
              borderTop:
                "1px solid",

              borderColor:
                "divider",

              "& .MuiTablePagination-toolbar":
                {
                  minHeight:
                    54,

                  px:
                    {
                      xs:
                        1,

                      sm:
                        2,
                    },
                },
            }}
          />
        </Paper>
      </Box>


      {/* =================================================
          CREATE DIALOG
      ================================================= */}

      <Dialog
        open={
          open
        }
        onClose={
          handleClose
        }
        fullWidth
        maxWidth="lg"
        PaperProps={{
          sx: {
            borderRadius:
              {
                xs:
                  0,

                sm:
                  4,
              },

            maxHeight:
              {
                xs:
                  "100dvh",

                sm:
                  "90dvh",
              },

            m:
              {
                xs:
                  0,

                sm:
                  2,
              },
          },
        }}
      >
        <DialogTitle>
          <Typography
            fontWeight={
              900
            }
            fontSize={
              20
            }
          >
            Nouvelle facture fournisseur
          </Typography>

          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              mt:
                0.35,
            }}
          >
            Créez la facture à partir d'une réception déjà contrôlée.
          </Typography>
        </DialogTitle>


        <DialogContent
          dividers
        >
          <Stack
            spacing={
              2.5
            }
            sx={{
              pt:
                0.5,
            }}
          >
            <Alert
              severity="info"
              icon={
                <LockRoundedIcon />
              }
            >
              Sélectionnez d'abord une réception contrôlée. RLSys récupérera
              automatiquement le fournisseur, le bon de commande, les produits et
              les quantités réellement acceptées.
            </Alert>


            <TextField
              select
              required
              size="small"
              label="Réception contrôlée"
              value={
                formData.purchase_receipt_id
              }
              onChange={(
                event
              ) =>
                handleReceiptChange(
                  event.target
                    .value
                )
              }
              fullWidth
            >
              {receipts.length ===
              0 ? (
                <MenuItem
                  disabled
                  value=""
                >
                  Aucune réception disponible pour facturation
                </MenuItem>
              ) : (
                receipts.map(
                  (
                    receipt
                  ) => (
                    <MenuItem
                      key={
                        receipt.id
                      }
                      value={
                        receipt.id
                      }
                    >
                      {receipt.code} •{" "}
                      {receipt.supplier_name} •{" "}
                      {receipt.purchase_order_code}
                    </MenuItem>
                  )
                )
              )}
            </TextField>


            {selectedReceipt ? (
              <>
                <Paper
                  variant="outlined"
                  sx={{
                    p:
                      2,

                    borderRadius:
                      3,

                    bgcolor:
                      "#f8fafc",
                  }}
                >
                  <Box
                    sx={{
                      display:
                        "grid",

                      gridTemplateColumns:
                        {
                          xs:
                            "1fr",

                          md:
                            "repeat(3, minmax(0, 1fr))",
                        },

                      gap:
                        2,
                    }}
                  >
                    <Box>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                      >
                        Fournisseur
                      </Typography>

                      <Typography
                        fontWeight={
                          800
                        }
                      >
                        {selectedReceipt.supplier_code
                          ? `${selectedReceipt.supplier_code} - `
                          : ""}
                        {selectedReceipt.supplier_name ||
                          "-"}
                      </Typography>
                    </Box>

                    <Box>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                      >
                        Bon de commande
                      </Typography>

                      <Typography
                        fontWeight={
                          800
                        }
                      >
                        {selectedReceipt.purchase_order_code ||
                          "-"}
                      </Typography>
                    </Box>

                    <Box>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                      >
                        Entrepôt
                      </Typography>

                      <Typography
                        fontWeight={
                          800
                        }
                      >
                        {selectedReceipt.warehouse_code
                          ? `${selectedReceipt.warehouse_code} - `
                          : ""}
                        {selectedReceipt.warehouse_name ||
                          "-"}
                      </Typography>
                    </Box>
                  </Box>
                </Paper>


                <Box
                  sx={{
                    display:
                      "grid",

                    gridTemplateColumns:
                      {
                        xs:
                          "1fr",

                        md:
                          "repeat(3, minmax(0, 1fr))",
                      },

                    gap:
                      2,
                  }}
                >
                  <TextField
                    required
                    size="small"
                    label="N° facture fournisseur"
                    name="supplier_invoice_number"
                    value={
                      formData.supplier_invoice_number
                    }
                    onChange={
                      handleChange
                    }
                    helperText="Numéro figurant sur la facture physique du fournisseur"
                    fullWidth
                  />

                  <TextField
                    type="date"
                    size="small"
                    label="Date facture"
                    name="invoice_date"
                    value={
                      formData.invoice_date
                    }
                    onChange={
                      handleChange
                    }
                    InputLabelProps={{
                      shrink:
                        true,
                    }}
                    fullWidth
                  />

                  <TextField
                    type="date"
                    size="small"
                    label="Date échéance"
                    name="due_date"
                    value={
                      formData.due_date
                    }
                    onChange={
                      handleChange
                    }
                    InputLabelProps={{
                      shrink:
                        true,
                    }}
                    fullWidth
                  />
                </Box>


                <Box
                  sx={{
                    display:
                      "grid",

                    gridTemplateColumns:
                      {
                        xs:
                          "1fr",

                        sm:
                          "repeat(2, minmax(0, 1fr))",
                      },

                    gap:
                      2,
                  }}
                >
                  <TextField
                    select
                    size="small"
                    label="Devise"
                    name="currency"
                    value={
                      formData.currency
                    }
                    onChange={
                      handleChange
                    }
                    fullWidth
                  >
                    <MenuItem
                      value="USD"
                    >
                      USD
                    </MenuItem>

                    <MenuItem
                      value="CDF"
                    >
                      CDF
                    </MenuItem>
                  </TextField>

                  <TextField
                    type="number"
                    size="small"
                    label="Taux CDF / USD"
                    name="exchange_rate"
                    value={
                      formData.exchange_rate
                    }
                    onChange={
                      handleChange
                    }
                    inputProps={{
                      min:
                        0.000001,
                    }}
                    fullWidth
                  />
                </Box>


                <Divider />


                <Box>
                  <Typography
                    variant="h6"
                    fontWeight={
                      900
                    }
                  >
                    Produits facturés
                  </Typography>

                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      mt:
                        0.4,
                    }}
                  >
                    Les quantités proviennent de la réception contrôlée. Ajustez
                    uniquement les données qui figurent réellement sur la facture
                    du fournisseur.
                  </Typography>
                </Box>


                {formData.items.map(
                  (
                    item,
                    index
                  ) => (
                    <Paper
                      key={`${item.product_id}-${index}`}
                      variant="outlined"
                      sx={{
                        p:
                          2,

                        borderRadius:
                          3,
                      }}
                    >
                      <Box
                        sx={{
                          display:
                            "grid",

                          gridTemplateColumns:
                            {
                              xs:
                                "1fr",

                              md:
                                "minmax(260px, 2fr) repeat(4, minmax(110px, 1fr))",
                            },

                          gap:
                            1.5,

                          alignItems:
                            "start",
                        }}
                      >
                        <TextField
                          size="small"
                          label="Produit"
                          value={
                            `${item.product_code || ""} - ${item.product_name || ""}`
                          }
                          disabled
                          fullWidth
                        />

                        <TextField
                          size="small"
                          label={`Quantité (${item.unit})`}
                          value={
                            item.quantity
                          }
                          disabled
                          fullWidth
                        />

                        <TextField
                          type="number"
                          size="small"
                          label={`Coût / ${item.unit}`}
                          value={
                            item.unit_cost
                          }
                          onChange={(
                            event
                          ) =>
                            handleItemChange(
                              index,
                              "unit_cost",
                              event.target
                                .value
                            )
                          }
                          inputProps={{
                            min:
                              0,

                            step:
                              "0.01",
                          }}
                          fullWidth
                        />

                        <TextField
                          type="number"
                          size="small"
                          label="Remise %"
                          value={
                            item.discount_percent
                          }
                          onChange={(
                            event
                          ) =>
                            handleItemChange(
                              index,
                              "discount_percent",
                              event.target
                                .value
                            )
                          }
                          inputProps={{
                            min:
                              0,

                            max:
                              100,
                          }}
                          fullWidth
                        />

                        <TextField
                          type="number"
                          size="small"
                          label="Taxe %"
                          value={
                            item.tax_percent
                          }
                          onChange={(
                            event
                          ) =>
                            handleItemChange(
                              index,
                              "tax_percent",
                              event.target
                                .value
                            )
                          }
                          inputProps={{
                            min:
                              0,
                          }}
                          fullWidth
                        />
                      </Box>

                      <Typography
                        variant="caption"
                        color="success.main"
                        sx={{
                          mt:
                            1,

                          display:
                            "block",

                          fontWeight:
                            700,
                        }}
                      >
                        Quantité contrôlée et acceptée :{" "}
                        {item.accepted_quantity}{" "}
                        {item.unit}
                      </Typography>
                    </Paper>
                  )
                )}


                <Divider />


                <Box
                  sx={{
                    display:
                      "grid",

                    gridTemplateColumns:
                      {
                        xs:
                          "1fr",

                        sm:
                          "repeat(2, minmax(0, 1fr))",

                        lg:
                          "repeat(4, minmax(0, 1fr))",
                      },

                    gap:
                      2,
                  }}
                >
                  <TextField
                    type="number"
                    size="small"
                    label="Remise globale"
                    name="discount_amount"
                    value={
                      formData.discount_amount
                    }
                    onChange={
                      handleChange
                    }
                    fullWidth
                  />

                  <TextField
                    type="number"
                    size="small"
                    label="Taxe globale"
                    name="tax_amount"
                    value={
                      formData.tax_amount
                    }
                    onChange={
                      handleChange
                    }
                    fullWidth
                  />

                  <TextField
                    type="number"
                    size="small"
                    label="Transport"
                    name="transport_cost"
                    value={
                      formData.transport_cost
                    }
                    onChange={
                      handleChange
                    }
                    fullWidth
                  />

                  <TextField
                    type="number"
                    size="small"
                    label="Autres frais"
                    name="other_cost"
                    value={
                      formData.other_cost
                    }
                    onChange={
                      handleChange
                    }
                    fullWidth
                  />
                </Box>


                <TextField
                  multiline
                  minRows={
                    3
                  }
                  label="Notes"
                  name="notes"
                  value={
                    formData.notes
                  }
                  onChange={
                    handleChange
                  }
                  fullWidth
                />


                <Paper
                  variant="outlined"
                  sx={{
                    p:
                      2,

                    borderRadius:
                      3,

                    bgcolor:
                      "#f8fafc",
                  }}
                >
                  <Box
                    sx={{
                      display:
                        "flex",

                      flexDirection:
                        {
                          xs:
                            "column",

                          sm:
                            "row",
                        },

                      justifyContent:
                        "space-between",

                      gap:
                        1.5,
                    }}
                  >
                    <Box>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                      >
                        Sous-total calculé
                      </Typography>

                      <Typography
                        fontWeight={
                          800
                        }
                      >
                        {formatAmount(
                          itemsSubtotal
                        )}{" "}
                        {formData.currency}
                      </Typography>
                    </Box>

                    <Box>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                      >
                        Total facture
                      </Typography>

                      <Typography
                        variant="h6"
                        fontWeight={
                          900
                        }
                        color="#0f172a"
                      >
                        {formatAmount(
                          total
                        )}{" "}
                        {formData.currency}
                      </Typography>
                    </Box>
                  </Box>
                </Paper>
              </>
            ) : null}
          </Stack>
        </DialogContent>


        <DialogActions
          sx={{
            px:
              3,

            py:
              2,

            borderTop:
              "1px solid",

            borderColor:
              "divider",
          }}
        >
          <Button
            onClick={
              handleClose
            }
            disabled={
              saving
            }
          >
            Annuler
          </Button>

          <Button
            variant="contained"
            disabled={
              saving ||
              !selectedReceipt
            }
            onClick={
              handleSave
            }
          >
            {saving
              ? "Enregistrement..."
              : "Créer la facture"}
          </Button>
        </DialogActions>
      </Dialog>


      {/* =================================================
          SNACKBAR
      ================================================= */}

      <Snackbar
        open={
          snackbar.open
        }
        autoHideDuration={
          4000
        }
        onClose={() =>
          setSnackbar(
            (
              previous
            ) => ({
              ...previous,
              open:
                false,
            })
          )
        }
        anchorOrigin={{
          vertical:
            "top",

          horizontal:
            "right",
        }}
      >
        <Alert
          severity={
            snackbar.severity
          }
          variant="filled"
          onClose={() =>
            setSnackbar(
              (
                previous
              ) => ({
                ...previous,

                open:
                  false,
              })
            )
          }
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </MainLayout>
  )
}


export default SupplierInvoicesPage